import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/agent/AgentPanels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Gauge, Plus, Trash2, Save, RefreshCw } from "lucide-react";

const OPS = [["gte", "is at least"], ["lte", "is at most"], ["between", "is between (min,max)"], ["eq", "equals"], ["in", "is one of (comma list)"], ["exists", "is filled in"], ["true", "is yes"]];
const FIELDS = {
  loan: ["required_amount", "pincode_serviceable", "employment_type", "monthly_income", "email", "consent.given", "credit_score.value", "docs_accepted", "last_outcome", "lead_source", "loan_type", "campaign"],
  insurance: ["temperature", "source", "email", "insurance_type", "pincode_serviceable", "last_outcome", "priority", "city"],
};
const valToStr = (r) => (Array.isArray(r.value) ? r.value.join(", ") : r.value ?? "");
const strToVal = (op, s) => {
  if (op === "exists" || op === "true") return null;
  if (op === "in") return s.split(",").map((x) => x.trim()).filter(Boolean);
  if (op === "between") return s.split(",").map((x) => Number(x.trim()));
  return op === "eq" ? s : Number(s);
};

function RulesEditor({ business, canEdit }) {
  const [cfg, setCfg] = useState(null);
  const load = useCallback(() => api.get(`/scoring/${business}`).then(({ data }) => setCfg({ ...data, rules: data.rules.map((r) => ({ ...r, _v: valToStr(r) })) })), [business]);
  useEffect(() => { load(); }, [load]);
  if (!cfg) return null;
  const set = (i, k, v) => setCfg((c) => ({ ...c, rules: c.rules.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const save = async () => {
    try {
      await api.put(`/scoring/${business}`, { thresholds: cfg.thresholds, rules: cfg.rules.map((r) => ({ id: r.id, label: r.label, field: r.field, op: r.op, points: Number(r.points), value: strToVal(r.op, r._v || "") })) });
      toast.success("Rules saved. Click Recalculate to apply them to existing leads.");
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  const recalc = async () => {
    try { const { data } = await api.post(`/scoring/${business}/recalculate`); toast.success(`${data.rescored} leads rescored`); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  return (
    <Card title={business === "loan" ? "Loan lead rules" : "Insurance lead rules"} actions={canEdit && <>
      <Button size="sm" variant="outline" onClick={() => setCfg({ ...cfg, rules: [...cfg.rules, { label: "", field: FIELDS[business][0], op: "exists", points: 5, _v: "" }] })} data-testid="score-add-rule-btn"><Plus className="h-4 w-4 mr-1" />Add rule</Button>
      <Button size="sm" variant="outline" onClick={recalc} data-testid="score-recalc-btn"><RefreshCw className="h-4 w-4 mr-1" />Recalculate all</Button>
      <Button size="sm" onClick={save} data-testid="score-save-btn"><Save className="h-4 w-4 mr-1" />Save</Button>
    </>}>
      <div className="flex flex-wrap gap-4 mb-3 text-sm">
        <Label className="flex items-center gap-2">High Priority at <Input type="number" className="w-20 h-8" value={cfg.thresholds.high} disabled={!canEdit} onChange={(e) => setCfg({ ...cfg, thresholds: { ...cfg.thresholds, high: e.target.value } })} data-testid="score-high-input" /> points</Label>
        <Label className="flex items-center gap-2">Qualified at <Input type="number" className="w-20 h-8" value={cfg.thresholds.qualified} disabled={!canEdit} onChange={(e) => setCfg({ ...cfg, thresholds: { ...cfg.thresholds, qualified: e.target.value } })} data-testid="score-qualified-input" /> points</Label>
      </div>
      <p className="text-xs text-muted-foreground mb-2">Duplicates are always marked Duplicate. Scores only guide the call order: they never approve or reject anyone, and self-reported credit scores stay unverified.</p>
      {cfg.rules.map((r, i) => (
        <div key={r.id || i} className="grid grid-cols-12 gap-2 items-center py-1.5 border-b border-border" data-testid="score-rule-row">
          <Input className="col-span-12 md:col-span-3 h-8" value={r.label} disabled={!canEdit} onChange={(e) => set(i, "label", e.target.value)} placeholder="Reason shown to callers" data-testid="score-rule-label" />
          <Select value={r.field} disabled={!canEdit} onValueChange={(v) => set(i, "field", v)}><SelectTrigger className="col-span-6 md:col-span-2 h-8 font-mono text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{FIELDS[business].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
          <Select value={r.op} disabled={!canEdit} onValueChange={(v) => set(i, "op", v)}><SelectTrigger className="col-span-6 md:col-span-2 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{OPS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <Input className="col-span-6 md:col-span-3 h-8 text-xs" value={r._v} disabled={!canEdit || ["exists", "true"].includes(r.op)} onChange={(e) => set(i, "_v", e.target.value)} placeholder="Value" />
          <Input type="number" className="col-span-4 md:col-span-1 h-8" value={r.points} disabled={!canEdit} onChange={(e) => set(i, "points", e.target.value)} data-testid="score-rule-points" />
          {canEdit && <Button size="icon" variant="ghost" className="col-span-2 md:col-span-1 h-8 w-8 text-rose-600 justify-self-end" onClick={() => setCfg({ ...cfg, rules: cfg.rules.filter((_, j) => j !== i) })} data-testid="score-rule-remove"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
    </Card>
  );
}

function Outcomes({ canEdit }) {
  const [items, setItems] = useState("");
  useEffect(() => { api.get("/workspace/outcomes").then(({ data }) => setItems(data.join(", "))); }, []);
  const save = async () => {
    try { await api.put("/workspace/outcomes", { items: items.split(",").map((s) => s.trim()).filter(Boolean) }); toast.success("Call results saved"); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  return (
    <Card title="Call results callers can pick" actions={canEdit && <Button size="sm" onClick={save} data-testid="outcomes-save-btn"><Save className="h-4 w-4 mr-1" />Save</Button>}>
      <Input value={items} disabled={!canEdit} onChange={(e) => setItems(e.target.value)} data-testid="outcomes-input" />
      <p className="text-xs text-muted-foreground mt-2">Comma-separated. "Call back later" always needs a follow-up time. "Connected" and "Interested" count towards the score.</p>
    </Card>
  );
}

export default function LeadScoring() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("scoring:manage");
  return (
    <div className="space-y-4">
      <PageHeader title="Lead Quality Rules" subtitle="Points per rule. Every lead shows its score, category and the reasons" icon={Gauge} />
      <Tabs defaultValue="loan">
        <TabsList><TabsTrigger value="loan" data-testid="score-tab-loan">Loan</TabsTrigger><TabsTrigger value="insurance" data-testid="score-tab-insurance">Insurance</TabsTrigger><TabsTrigger value="outcomes" data-testid="score-tab-outcomes">Call results</TabsTrigger></TabsList>
        <TabsContent value="loan"><RulesEditor business="loan" canEdit={canEdit} /></TabsContent>
        <TabsContent value="insurance"><RulesEditor business="insurance" canEdit={canEdit} /></TabsContent>
        <TabsContent value="outcomes"><Outcomes canEdit={canEdit} /></TabsContent>
      </Tabs>
    </div>
  );
}
